import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';
import LanguageSwitcher from '../Common/LanguageSwitcher';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navItems = [
    { key: 'investment', to: { pathname: '/', hash: '#investment' } },
    { key: 'construction', to: { pathname: '/', hash: '#construction' } },
    { key: 'materials', to: { pathname: '/', hash: '#materials' } },
    { key: 'sustainability', to: { pathname: '/', hash: '#sustainability' } },
  ];

  const isActiveNavItem = (item) => {
    if (item.to.hash) {
      return location.pathname === '/' && location.hash === item.to.hash;
    }
    return location.pathname === '/' && !location.hash;
  };

  return (
    <motion.header
      className={`header ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container">
        <div className="header-content">
          <div
            className="logo"
            role="link"
            tabIndex={0}
            onClick={() => {
              setIsMenuOpen(false);
              navigate('/');
              try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { window.scrollTo(0, 0); }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsMenuOpen(false);
                navigate('/');
                try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (err) { window.scrollTo(0, 0); }
              }
            }}
          >
            <motion.div
              className="logo-container"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <img src="/dldmn-logo-final.svg" alt="DLDMN Baumanagement GmbH" className="logo-image" />
            </motion.div>
          </div>

          <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
            <ul className="nav-list">
              {navItems.map((item) => (
                <motion.li
                  key={item.key}
                  whileHover={{ y: -2 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <Link
                    to={item.to}
                    className={`nav-link ${isActiveNavItem(item) ? 'active' : ''}`}
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                </motion.li>
              ))}
            </ul>
            <div className="nav-actions">
              <LanguageSwitcher />
            </div>
          </nav>

          <button
            className="mobile-menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
